import React, { FC, useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { routes } from "@app/routing";
import { getReadableComboOrderTestTypesList, useQuery } from "@app/utils";
import WelcomeDobScreen from "./welcome";
import WelcomeDobVerificationFailedScreen from "./welcome/welcomeDobVerificationFailedScreen";
import {
  BloodDrawProvider,
  DrawRequestContext,
  DrawRequestProvider,
  ErrorProvider,
  HeapAnalyticDataProvider,
  IntlContext,
  KitProvider,
  OrderRenewalProvider,
  ProfileProvider,
} from "@app/provider";
import OrderInWrongStateScreen from "@app/pages/public/scheduleBloodDraw/orderInWrongState/OrderInWrongStateScreen";
import { PersonalInformation } from "./personalInformation/personalInformation";
import {
  CreateBloodDrawFailedScreen,
  ScheduleBloodDrawStepper,
} from "./scheduleBloodDrawStepper";
import { KitDetails } from "./kitDetails";
import { ViewBloodDrawDetails } from "./viewBloodDrawDetails";
import TimingInfoDialog from "@app/components/sampleDraw/steps/kit/timingInfoDialog";
import { useDialog, useErrorController } from "@natera/platform/lib/hooks";
import { ComboOrderModal } from "@app/components/sampleDraw/dialogs";
import { DrawRequestFlow } from "@app/provider/drawRequest";
import { useSetUserLanguage, useTokenizedLinks } from "@app/hooks";

export enum ScheduleBloodDrawScreens {
  WELCOME_DOB_VERIFY_SCREEN = "WELCOME_DOB_VERIFY_SCREEN",
  WELCOME_DOB_VERIFY_FAILED_SCREEN = "WELCOME_DOB_VERIFY_FAILED_SCREEN",
  SCHEDULE_BLOOD_DRAW_FAILED_SCREEN = "SCHEDULE_BLOOD_DRAW_FAILED_SCREEN",
  PERSONAL_INFO_SCREEN = "PERSONAL_INFO_SCREEN",
  KIT_DETAILS_SCREEN = "KIT_DETAILS_SCREEN",
  MOBILE_PHLEBOTOMY_STEPPER_SCREEN = "MOBILE_PHLEBOTOMY_STEPPER_SCREEN",
  CREATE_BLOOD_DRAW_FAILED_SCREEN = "CREATE_BLOOD_DRAW_FAILED_SCREEN",
  VIEW_BLOOD_DRAW_DETAILS = "VIEW_BLOOD_DRAW_DETAILS",
}

interface ScheduleBloodDraw {
  initialScreen: ScheduleBloodDrawScreens;
  token?: string;
  flow?: DrawRequestFlow;
}

export const ScheduleBloodDraw: FC<ScheduleBloodDraw> = ({
  initialScreen,
  token,
  flow = DrawRequestFlow.GUEST,
}) => {
  const navigate = useNavigate();

  const { getTokenLanguage } = useTokenizedLinks();
  const setUserLanguage = useSetUserLanguage();

  useEffect(() => {
    if (!token) {
      navigate(routes.home, { replace: true });
    }
  }, []);

  const { verifiedSampleDrawData } = React.useContext(DrawRequestContext);

  const comboOrderModal = useDialog(ComboOrderModal);
  const timingDialog = useDialog(TimingInfoDialog);
  const errorController = useErrorController();

  const [bloodDrawScreen, setBloodDrawScreen] =
    React.useState<ScheduleBloodDrawScreens>(initialScreen);

  const [initialDialogsHasBeenProcessed, setInitialDialogsHasBeenProcessed] =
    React.useState<boolean>(false);

  useEffect(() => {
    const isSampleBloodDrawStepperScreen = [
      ScheduleBloodDrawScreens.KIT_DETAILS_SCREEN,
      ScheduleBloodDrawScreens.MOBILE_PHLEBOTOMY_STEPPER_SCREEN,
    ].includes(bloodDrawScreen);

    if (
      !initialDialogsHasBeenProcessed &&
      isSampleBloodDrawStepperScreen &&
      verifiedSampleDrawData
    ) {
      const isOncologyOrder =
        verifiedSampleDrawData?.sampleInfo.sample?.isOncologyOrder;
      const testTypes =
        verifiedSampleDrawData?.sampleInfo.sample?.testTypes ?? [];

      //timing dialog
      if (isOncologyOrder) {
        const closeTimingDialogAndRedirect = () => {
          timingDialog.close();
          window.history.length > 1 ? navigate(-1) : navigate(routes.home);
        };

        timingDialog.open({
          onClose: closeTimingDialogAndRedirect,
          onContinue: timingDialog.close,
        });
      }

      //combo order dialog
      if (testTypes.length > 1) {
        const orderTypes = getReadableComboOrderTestTypesList(testTypes);
        comboOrderModal.open({ orderTypes, onClose: comboOrderModal.close });
      }

      setInitialDialogsHasBeenProcessed(true);
    }
  }, [initialDialogsHasBeenProcessed, bloodDrawScreen, verifiedSampleDrawData]);

  useEffect(() => {
    if (token) {
      getTokenLanguage(token).then((lang) => setUserLanguage(lang));
    }
  }, [token]);

  const renderScreen = () => {
    switch (bloodDrawScreen) {
      case ScheduleBloodDrawScreens.WELCOME_DOB_VERIFY_FAILED_SCREEN:
        return <WelcomeDobVerificationFailedScreen />;
      case ScheduleBloodDrawScreens.SCHEDULE_BLOOD_DRAW_FAILED_SCREEN:
        return <OrderInWrongStateScreen />;
      case ScheduleBloodDrawScreens.PERSONAL_INFO_SCREEN:
        return <PersonalInformation setBloodDrawScreen={setBloodDrawScreen} />;
      case ScheduleBloodDrawScreens.KIT_DETAILS_SCREEN:
        return (
          <KitDetails
            token={token as string}
            setBloodDrawScreen={setBloodDrawScreen}
            flow={flow}
          />
        );
      case ScheduleBloodDrawScreens.MOBILE_PHLEBOTOMY_STEPPER_SCREEN:
        return (
          <ScheduleBloodDrawStepper
            token={token as string}
            setBloodDrawScreen={setBloodDrawScreen}
            flow={flow}
          />
        );
      case ScheduleBloodDrawScreens.CREATE_BLOOD_DRAW_FAILED_SCREEN:
        return <CreateBloodDrawFailedScreen />;
      case ScheduleBloodDrawScreens.VIEW_BLOOD_DRAW_DETAILS:
        return (
          <ViewBloodDrawDetails
            token={token as string}
            isPublicRequest={flow === DrawRequestFlow.PUBLIC_MP}
          />
        );
      default:
        return (
          <ErrorProvider controller={errorController}>
            <WelcomeDobScreen
              token={token as string}
              setBloodDrawScreen={setBloodDrawScreen}
            />
          </ErrorProvider>
        );
    }
  };

  return (
    <>
      {renderScreen()}
      {timingDialog.getDialog()}
      {comboOrderModal.getDialog()}
    </>
  );
};

const ScheduleBloodDraw$: FC = () => {
  const query = useQuery();
  const token = query.get("token") ?? undefined;

  return (
    <HeapAnalyticDataProvider>
      <BloodDrawProvider>
        <DrawRequestProvider>
          <ProfileProvider>
            <KitProvider>
              <OrderRenewalProvider>
                <ScheduleBloodDraw
                  initialScreen={
                    ScheduleBloodDrawScreens.WELCOME_DOB_VERIFY_SCREEN
                  }
                  token={token}
                />
              </OrderRenewalProvider>
            </KitProvider>
          </ProfileProvider>
        </DrawRequestProvider>
      </BloodDrawProvider>
    </HeapAnalyticDataProvider>
  );
};

export default ScheduleBloodDraw$;
